import React, { useEffect, useState, useId } from "react";
import styles from "./UnitModal.module.scss";
import { createPortal } from "react-dom";
import CloseIcon from "../../../assets/icons/close.svg?react";
import { useModalContext } from "../../../components/modal-context/ModalContext";
import TranslationBadge from "../../../components/translation-provider/TranslationBadge";
import { useUser } from "@src/hooks/useUser";
import { usePresentationContext } from "@src/hooks/usePresentationContext";
import { logger } from "../../../utils/logger";

type Props = {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  global?: boolean;
  noPadding?: boolean;
  closeButton?: boolean;
  className?: string;
  onScroll?: () => void;
  isLoading?: boolean;
};

function UnitModal(props: Props) {
  const {
    open,
    onClose,
    children,
    global = true,
    closeButton = true,
    className = "",
    noPadding,
    onScroll,
    isLoading,
  } = props;
  const [isWindows, setIswindows] = useState(false);
  const generatedId = useId();
  const modalId = `unit-modal-${generatedId}`;
  const platform = window.navigator.platform;
  const { registerModal, unregisterModal } = useModalContext();
  const { isPreview } = usePresentationContext();
  const [user] = useUser();

  // Check if user is from Australia
  const isAUuser = user?.id?.toLowerCase().startsWith("au");

  useEffect(() => {
    if (["Win3", "Win64", "Windows", "Win32", "Win"].indexOf(platform) !== -1) {
      setIswindows(true);
    }
  }, [platform]);

  // Register/unregister modal with context when opened/closed
  useEffect(() => {
    if (open) {
      logger.info("[UnitModal] Registering modal with ID:", modalId);
      registerModal(modalId);
    } else {
      logger.info("[UnitModal] Unregistering modal with ID:", modalId);
      unregisterModal(modalId);
    }

    // Clean up on unmount
    return () => {
      logger.info(
        "[UnitModal] Cleanup - unregistering modal with ID:",
        modalId,
      );
      unregisterModal(modalId);
    };
  }, [modalId, open, registerModal, unregisterModal]);

  const renderContent = (
    <div
      id="modal-body"
      className={`${styles.inner} ${className} ${isWindows && styles.innerWindows} ${noPadding && styles.innerOverride} ${isLoading ? styles.loading : ""}`}
      onScroll={onScroll}
    >
      {/* Add translation badge to modal - hidden for Australian users */}
      {!isAUuser && !isPreview && <TranslationBadge type="modal" />}
      {children}
      {closeButton && (
        <button onClick={onClose} className={styles.closeWrapper}>
          <CloseIcon className={styles.closeIcon} />
        </button>
      )}
    </div>
  );

  if (!open) return null;

  if (global) {
    return createPortal(
      <div className={styles.global}>
        <div className={styles.backdrop} onClick={onClose} />
        {renderContent}
      </div>,
      document.body,
    );
  }

  return (
    <div className={styles.local}>
      <div className={styles.backdrop} onClick={onClose} />
      {renderContent}
    </div>
  );
}

export default UnitModal;
